import React from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  BUSINESS_INFO, 
  getOrganizationSchema, 
  getLocalBusinessSchema,
  getWebsiteSchema,
  getBreadcrumbSchema,
  getProductSchema,
  PAGE_SEO,
  getProductSEO,
  getCategorySEO
} from '../utils/seoConfig';

/**
 * SEO Component for dynamic meta tags and structured data
 */
const SEOHead = ({ 
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  product = null,
  category = null,
  breadcrumbs = null,
  noindex = false
}) => {
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const currentUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const ogImage = image || BUSINESS_INFO.logo;
  
  // Get page-specific SEO if category provided
  let pageSEO = {};
  if (category && PAGE_SEO[category]) {
    pageSEO = PAGE_SEO[category];
  } else if (product) {
    pageSEO = getProductSEO(product);
  }
  
  const finalTitle = title || pageSEO.title || PAGE_SEO.home.title;
  const finalDescription = description || pageSEO.description || PAGE_SEO.home.description;
  const finalKeywords = keywords || pageSEO.keywords || PAGE_SEO.home.keywords;

  // Build structured data array
  const structuredData = [
    getOrganizationSchema(),
    getLocalBusinessSchema(),
    getWebsiteSchema()
  ];

  // Add product schema if product page
  if (product) {
    structuredData.push(getProductSchema(product, currentUrl));
  }

  // Add breadcrumb schema if provided
  if (breadcrumbs && breadcrumbs.length > 0) {
    structuredData.push(getBreadcrumbSchema(breadcrumbs));
  }

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="title" content={finalTitle} />
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      <meta name="author" content={BUSINESS_INFO.name} />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={currentUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type === 'product' ? 'product' : 'website'} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={finalTitle} />
      <meta property="og:site_name" content={BUSINESS_INFO.name} />
      <meta property="og:locale" content="en_IN" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Product specific meta (if product page) */}
      {product && (
        <>
          <meta property="product:price:amount" content={product.total_price || product.base_price || 0} />
          <meta property="product:price:currency" content="INR" />
          <meta property="product:availability" content={product.is_sold_out ? 'out of stock' : 'in stock'} />
          <meta property="product:brand" content={BUSINESS_INFO.name} />
          <meta property="product:category" content={product.category} />
        </>
      )}
      
      {/* Geographic Meta Tags for India */}
      <meta name="geo.region" content="IN" />
      <meta name="geo.country" content="India" />
      <meta name="ICBM" content="20.5937, 78.9629" />
      
      {/* Mobile Meta */}
      <meta name="format-detection" content="telephone=yes" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content={BUSINESS_INFO.name} />
      
      {/* Theme Color */}
      <meta name="theme-color" content="#0a1628" />
      <meta name="msapplication-navbutton-color" content="#0a1628" />
      
      {/* Structured Data */}
      {structuredData.map((schema, index) => (
        <script key={`schema-${schema['@type'] || index}`} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEOHead;

/**
 * Pre-built SEO components for common pages
 */
export const HomeSEO = () => (
  <SEOHead 
    title={PAGE_SEO.home.title}
    description={PAGE_SEO.home.description}
    keywords={PAGE_SEO.home.keywords}
    breadcrumbs={[
      { name: 'Home', url: typeof window !== 'undefined' ? window.location.origin : '' }
    ]}
  />
);

export const ProductsSEO = ({ category }) => {
  const seo = category ? getCategorySEO(category) : PAGE_SEO.products;
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : '';
  
  return (
    <SEOHead 
      title={seo.title}
      description={seo.description}
      keywords={seo.keywords}
      breadcrumbs={[
        { name: 'Home', url: siteUrl },
        { name: category ? category.charAt(0).toUpperCase() + category.slice(1) : 'Products', url: `${siteUrl}/products${category ? `?category=${category}` : ''}` }
      ]}
    />
  );
};

export const ProductDetailSEO = ({ product }) => {
  if (!product) return null;
  
  const seo = getProductSEO(product);
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : '';
  
  return (
    <SEOHead 
      title={seo.title}
      description={seo.description}
      keywords={seo.keywords}
      image={product.images?.[0]}
      type="product"
      product={product}
      breadcrumbs={[
        { name: 'Home', url: siteUrl },
        { name: product.category?.charAt(0).toUpperCase() + product.category?.slice(1) || 'Products', url: `${siteUrl}/products?category=${product.category}` },
        { name: product.name, url: `${siteUrl}/product/${product.id}` }
      ]}
    />
  );
};

export const GoldSEO = () => (
  <SEOHead 
    title={PAGE_SEO.gold.title}
    description={PAGE_SEO.gold.description}
    keywords={PAGE_SEO.gold.keywords}
    category="gold"
  />
);

export const SilverSEO = () => (
  <SEOHead 
    title={PAGE_SEO.silver.title}
    description={PAGE_SEO.silver.description}
    keywords={PAGE_SEO.silver.keywords}
    category="silver"
  />
);

export const DiamondSEO = () => (
  <SEOHead 
    title={PAGE_SEO.diamond.title}
    description={PAGE_SEO.diamond.description}
    keywords={PAGE_SEO.diamond.keywords}
    category="diamond"
  />
);

export const PlatinumSEO = () => (
  <SEOHead 
    title={PAGE_SEO.platinum.title}
    description={PAGE_SEO.platinum.description}
    keywords={PAGE_SEO.platinum.keywords}
    category="platinum"
  />
);

export const MarketAnalysisSEO = () => (
  <SEOHead 
    title={PAGE_SEO.marketAnalysis.title}
    description={PAGE_SEO.marketAnalysis.description}
    keywords={PAGE_SEO.marketAnalysis.keywords}
  />
);
